/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Formik } from "formik";

import Colors from "constants/Colors";
import { GlobalAuthContext } from "store/context";
import { FitinsurAPI, MessageCenter } from "utils";
import { useTextMessage } from '@fitinsur/hooks';

import {
  Label,
  Icon,
  Text,
  ResponsiveTable,
  ListHeader,
  ButtonWithIcon,
  DialogAlertMessage,
  ModalInfo,
  ModalConfirmation,
  HasPermission,
} from '@fitinsur/components';

import { Button } from "components/index";

import { 
  LoaderContext 
} from '@fitinsur/contexts';

import {
  Content,
  LoadContainer,
  ContentTextarea,
  ButtonContainerRow,
  ButtonContent
} from "./styles";
import {   usePersonExpire, usePersonRestore } from "../../../../services/PersonService";

export function PersonList({ permissions }) {
  const { getDescription } = useTextMessage();

  const initialFilterData = [
    { label: getDescription("DOMAINS", 824, { textFormat: 'uppercase' }) || "NUMERO", orderKey: 'idPerson', key: 'idPerson', type: 'text' },
    { label: getDescription("DOMAINS", 914, { textFormat: 'uppercase' }) || "DATA DE CRIAÇÃO", orderKey: 'createDate', key: 'createDate', type: 'date' },
    { label: getDescription("DOMAINS", 910, { textFormat: 'uppercase' }) || "CNPJ/CPF", orderKey: 'legalPersonId', key: 'legalPersonId', type: 'text' },
    { label: getDescription("DOMAINS", 911, { textFormat: 'uppercase' }) || "RAZÃO SOCIAL/NOME COMPLETO", orderKey: 'personName', key: 'personName', type: 'text' },
    { label: getDescription("DOMAINS", 912, { textFormat: 'uppercase' }) || "TIPO DE PESSOA", orderKey: 'personType', key: 'personType', type: 'list', options: [{label:"FÍSICA" , value: 34},{label:"JURÍDICA" , value: 35}] },
    { label: getDescription("DOMAINS", 913, { textFormat: 'uppercase' }) || "PARTE PAI", orderKey: 'holding', key: 'holding', type: 'text' }
  ];

  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const history = useHistory();
  const location = useLocation();
  const formik = useRef();
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [persons, setPersons] = useState([]);
  const [typePerson, setTypePerson] = useState(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [isRestore, setIsRestore] = useState(null);
  const [personAdditionalInfoSelected, setPersonAdditionalInfoSelected] = useState([]);
  const [affectedEnquiries, setAffectedEnquiries] = useState([]);
  const [currentPerson, setCurrentPerson] = useState(null);
  const [currentPersonRelationship, setCurrentPersonRelationship] = useState(null);
  const [filterData, setFilterData] = useState(localStorage.getItem('access-person-filter') && localStorage.getItem('str-access-person-filter') && localStorage.getItem('str-access-person-filter') != ""
      ? JSON.parse(localStorage.getItem('access-person-filter'))
      : initialFilterData
  );

  const initialValues = {
    filter: {
      idPerson: "",
      createDate: "",
      legalPersonId: "",
      personName: "",
      idDomain: "",
      holding: ""
    },
  };

  const columns = [
    {
      title: getDescription("DOMAINS", 824, { textFormat: 'uppercase' }) || "NÚMERO",
      key: "idPerson",
      sortKey: 'idPerson',
      minWidth: 8,
      maxWidth: 8,
      component: (listItem) =>
        <Label fontSize={12}>
          {`${listItem.idPerson}`}
        </Label>
    },
    {
      title: getDescription("DOMAINS", 914, { textFormat: 'uppercase' }) || "DATA DE CRIAÇÃO",
      key: "createDate",
      sortKey: 'createDate',
      minWidth: 12,
      maxWidth: 12,
      component: (listItem) => <Label fontSize={12}>
        {new Date(listItem.createDate).toLocaleDateString('pt-br')}
      </Label>
    },
    {
      title: getDescription("DOMAINS", 910, { textFormat: 'uppercase' }) || "CNPJ/CPF",
      key: "legalPersonId",
      sortKey: 'legalPersonId',
      minWidth: 12,
      maxWidth: 12,
      component: (listItem) => <Label fontSize={12}>
        {listItem.legalPersonId}
      </Label>
    },
    {
      title: getDescription("DOMAINS", 911, { textFormat: 'uppercase' }) || "RAZÃO SOCIAL/NOME COMPLETO",
      key: "personName",
      sortKey: 'personName',
      minWidth: 18,
      maxWidth: 18,
      component: (listItem) => <Label fontSize={12}>
        {listItem.idDomain === 34 ? listItem.fullName : listItem.lastName}
      </Label>
    },
    {
      title: getDescription("DOMAINS", 912, { textFormat: 'uppercase' }) || "TIPO DE PESSOA",
      key: "idDomain",
      sortKey: 'idDomain',
      minWidth: 12,
      maxWidth: 12,
      component: (listItem) => <Label fontSize={12}>
        {getDescription("DOMAINS", listItem.idDomain)}
      </Label>
    },
    {
      title: getDescription("DOMAINS", 913, { textFormat: 'uppercase' }) || "PARTE PAI",
      key: "holding",
      sortKey: 'holding',
      minWidth: 18,
      maxWidth: 18,
      component: (listItem) => <Label fontSize={12}>
        {listItem.holding}
      </Label>
    },
    {
      title: getDescription("DOMAINS", 783, { textFormat: 'uppercase' }),
      key: "action",
      minWidth: 8,
      maxWidth: 8,
      paddingRight: 5,
      component: (listItem) => <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Icon
              onClick={() =>
                history.push(
                  `/management/58/person/edit/${listItem.idPerson}`
                )
              }
              name="edit"
              disabled={listItem.isDisabled}
              style={{
                alignSelf: "left",
                cursor: "pointer",
              }}
            />
            {
              !listItem.isDisabled ?
              <Icon
                onClick={() => getAffectedEnquiries(listItem.idPerson, listItem.legalPersonId)}
                title={getDescription("DOMAINS", 866)}
                name="disable-user"
                style={{
                  alignSelf: "right",
                  cursor: "pointer",
                }}
              /> :
              <Icon
                onClick={() => setIsRestore(listItem.idPerson)}
                title={getDescription("DOMAINS", 867)}
                name="restaure-user"
                style={{
                  alignSelf: "right",
                  cursor: "pointer",
                }}
              />
            }
          </div>
    }
  ];

  function resetFilters() {
    formik.current.resetForm(initialValues);
    setCurrentPage(1);
    setSortBy({ key: null, direction: null });
    handleFilter(initialValues.filter);
  }

  function handleSortList(key) {
    let direction = "asc";
    if (sortBy.key === key && sortBy.direction === "asc") direction = "desc";
    const sort = { key: key, direction: direction };

    setSortBy(sort);
    setCurrentPage(1);
    getPersons(1, sort, `${location.search}`);
  }

  const handleFilter = (resetFilter) => {
    if (resetFilter) {
      getPersons(1, {}, null);
      localStorage.removeItem("str-access-person-filter");
      localStorage.removeItem("access-person-filter");
      history.push(`/management/58/person`);
    } else {
      const queryString = localStorage.getItem("access-person-filter") 
        ? localStorage.getItem('str-access-person-filter') 
        : "";
      console.log(filterData);
      const filterQueryString = `?${queryString}`;
  
      setCurrentPage(1);
      getPersons(1, {}, filterQueryString);
  
      history.push(`/management/58/person${filterQueryString}`);
    }
  };

  async function getPersons(
    currentPage = 1,
    sort = {},
    filterQueryString = "?"
  ) {
    updateIsLoadingStatus(true);
    try {
      const url = `${filterQueryString || "?"}&sortBy=${JSON.stringify(
        sort
      )}&currentPage=${currentPage}`;
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur
        .requestGetPersonsList(url)
        .then((response) => response.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details);
        updateIsLoadingStatus(false);
        return;
      }
      console.log(response.ret_value);
      setPersons(response.ret_value);
      setTotalPages(response.pagination_control.totalPages);
      updateIsLoadingStatus(false);
    } catch (err) {
      console.log(err);
      updateIsLoadingStatus(false);
    }
  }

  function setNextPage() {
    if (currentPage === totalPages) return;
    setCurrentPage(currentPage + 1);
    getPersons(currentPage + 1, sortBy, `${location.search}`);
  }

  useEffect(() => {
    if (location.search) {
      getPersons(1, sortBy, `${location.search}`);
    } else {
      const queryString = localStorage.getItem('str-access-person-filter') || ""
      const filterQueryString = `?${queryString}`;
      getPersons(1, sortBy, filterQueryString);
    }
  }, []);

  const getAffectedEnquiries = (idPerson, legalPersonId) => {
    updateIsLoadingStatus(true);
    fitinsur.setAuthToken(authContext.token);
    if(legalPersonId) setTypePerson(legalPersonId.replace(/[^\d]/g, ''));
    fitinsur.requestListEnquiries(idPerson).then(x => x.json())
      .then(resp => {
        updateIsLoadingStatus(false);
        if (resp.ret_code === 200) {
          setCurrentPerson(idPerson)
          setAffectedEnquiries(resp.ret_value);
          setAlertDialogOpen(true);
        }
      }).catch(() => {
        updateIsLoadingStatus(false);
        mc.error(getDescription("DOMAINS", 915, 'uppercase'));
        fitinsur.setAuthToken(authContext.token);
      });
  };

  const {
    request: usePersonRestoreRequest,
  } = usePersonRestore();

  const {
    request: usePersonExpireRequest,
  } = usePersonExpire(); 

  const disableUser = async () => {
    try {
      updateIsLoadingStatus(true);
      const { data } = await usePersonExpireRequest(null, null, null, `v2/person/${currentPerson}`);
      if (data?.statusCode === 200) {
        updateIsLoadingStatus(false);
        setAlertDialogOpen(false);
        setCurrentPerson(null);
        getPersons(1, sortBy, `${location.search}`);
      };
    } catch (error) {
      updateIsLoadingStatus(false);
      mc.error(getDescription("DOMAINS", 915, 'uppercase'));
    }
  }

  const restoreUser = async () => {
    updateIsLoadingStatus(true);
    try {
      const { data } = await usePersonRestoreRequest(null, null, null, `v2/person/${isRestore}/restore`);
      if (data?.statusCode === 200) {
        updateIsLoadingStatus(false);
        setIsRestore(false);
        getPersons(1, sortBy, `${location.search}`);
      }
    } catch (error) {
      updateIsLoadingStatus(false);
      mc.error(getDescription("DOMAINS", 915, 'uppercase'));
    }
  }

  function renderModalApproval() {
    return (
      <DialogAlertMessage icon='exclamation' title="Atenção" size='16px' show={alertDialogOpen} setDialog={() => setAlertDialogOpen(false)}>
        <ContentTextarea>
          {
            (typePerson?.length <= 11 && !affectedEnquiries.length) ? 
            <>
              <Text>
              {getDescription("DOMAINS", 989, 'uppercase')}
              </Text>
              <ButtonContainerRow>
                  <ButtonContent><Button third onClick={() => setAlertDialogOpen(false)}>{getDescription("DOMAINS", 791, 'uppercase')}</Button></ButtonContent>
                  <ButtonContent>
                    <Button
                      secundary
                      onClick={() => disableUser()}>
                    {getDescription("DOMAINS", 792, 'uppercase')}
                    </Button>
                  </ButtonContent>
              </ButtonContainerRow>
            </>
            : affectedEnquiries.length ?
              <>
                <Text>
                {getDescription("DOMAINS", 916, 'uppercase')}
                </Text>
                <div style={{ maxHeight: 250, overflow: 'auto', marginTop: '10px' }}>
                  {affectedEnquiries.map((enquiry, index) => <p>{enquiry}</p>)}
                </div>
                <ButtonContainerRow>
                  <ButtonContent><Button secundary onClick={() => setAlertDialogOpen(false)}>{getDescription("DOMAINS", 791, 'uppercase')}</Button></ButtonContent>
                </ButtonContainerRow>
              </>
              :
              <>
                <Text>
                {getDescription("DOMAINS", 917, 'uppercase')}
                </Text>
                <ButtonContainerRow>
                  <ButtonContent><Button third onClick={() => setAlertDialogOpen(false)}>{getDescription("DOMAINS", 791, 'uppercase')}</Button></ButtonContent>
                  <ButtonContent>
                    <Button
                      secundary
                      onClick={() => disableUser()}>
                    {getDescription("DOMAINS", 792, 'uppercase')}
                    </Button>
                  </ButtonContent>
                </ButtonContainerRow>
              </>
          }
        </ContentTextarea>
      </DialogAlertMessage>
    )
  }

  return (
    <>
      <HasPermission permission={permissions.CREATE_PERSON}>
        <ButtonWithIcon
          onClick={() => history.push('/management/58/person/create')}
          icon={{ name: "plus-gray", width: '12px', height: '12px' }}
          title={getDescription("DOMAINS", 778, { withSkeleton: true, skeletonWidth: '80px', skeletonHeight: '16px' })}
        />
      </HasPermission>

      <Content>
        {showNotesModal && (
          <ModalInfo
            title={'Informações Adicionais'}
            setModalShow={setShowNotesModal}
            note={personAdditionalInfoSelected}
            disabled
          />
        )}
        <ModalConfirmation
          show={isRestore}
          text={getDescription("DOMAINS", 991)}
          showTitle={false}
          textButtonCancel={getDescription("DOMAINS", 791)}
          textButtonConfirm={getDescription("DOMAINS", 792)}
          onConfirm={() => { restoreUser() }}
          onCancel={() => { setIsRestore(false) }}
        />
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => (
            <Form>
              {renderModalApproval()}

              <ListHeader
                filterData={filterData}
                setFilterData={setFilterData}
                onHandleFilter={(resetFilter) => handleFilter(resetFilter)}
                listNameSpace={'access-person-filter'}
                listUrl='/management/58/person'
                isAdmin
                idDomainList={994}
                idApplication={675}
                filterNamePrefix={getDescription("DOMAINS", 1074, { textFormat: 'uppercase' })}
                defaultFilterName={getDescription("DOMAINS", 1075, { textFormat: 'uppercase' })}
              />

              <ResponsiveTable
                  onSortTable={handleSortList}
                  formikRef={formik?.current}
                  columns={columns}
                  data={persons}
                />

            </Form>
          )}
        </Formik>
        <LoadContainer>
          {!persons || persons.length === 0 || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              {getDescription("DOMAINS", 784, 'uppercase')}
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage()}>
              + {getDescription("DOMAINS", 785, 'uppercase')}
            </Button>
          )}
        </LoadContainer>
      </Content>
    </>
  )
}